import React from 'react';
import { motion } from 'framer-motion';
import { 
  Activity, 
  Clock, 
  Filter, 
  Globe, 
  Layers, 
  MessageCircle, 
  Package, 
  Bot, 
  Award, 
  ArrowRight,
  PlayCircle
} from 'lucide-react';
import { images } from '../assets/images';
import Section from '../components/Section';
import Button from '../components/Button';
import FeatureCard from '../components/FeatureCard';
import AppDownload from '../components/AppDownload';

const ExereyesHome: React.FC = () => {

  const scrollToDownload = () => {
    const element = document.getElementById('download');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      {/* HERO SECTION: Premium Dark Theme */}
      <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden bg-primary-900 text-white">
        
        {/* Arka Plan Efektleri */}
        <div className="absolute inset-0 z-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-secondary-500/10 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/3 mix-blend-screen"></div>
          <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-primary-500/20 rounded-full blur-[100px] translate-y-1/3 -translate-x-1/4 mix-blend-screen"></div>
          <div className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: 'radial-gradient(#fff 1px, transparent 1px)', backgroundSize: '30px 30px' }}></div>
        </div>

        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
            
            {/* Sol Taraf: Metin ve CTA */}
            <div className="lg:w-1/2 text-center lg:text-left">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="inline-flex items-center px-3 py-1 rounded-full bg-secondary-500/10 border border-secondary-500/20 text-secondary-300 text-sm font-medium mb-8 backdrop-blur-sm"
              >
                <span className="flex h-2 w-2 rounded-full bg-secondary-500 mr-2 animate-pulse"></span>
                Geleceğin Spor Deneyimi
              </motion.div>

              <motion.h1 
                className="text-4xl md:text-5xl lg:text-7xl font-display font-bold text-white mb-6 leading-tight"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                Spor Salonları <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-secondary-300 to-secondary-500">Exereyes</span> İle <br />
                Her Yerde
              </motion.h1>

              <motion.p 
                className="text-lg md:text-xl text-primary-100 mb-10 max-w-2xl mx-auto lg:mx-0 leading-relaxed"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                %100 yerli mühendislik ile geliştirilen özgün yapay zeka teknolojimiz, 
                evinizdeki sıradan nesneleri (sandalye, şişe, çanta, konserve, tava, tencere, masa vb.) egzersiz ekipmanına 
                dönüştürür.
              </motion.p>

              <motion.div 
                className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <Button 
                  variant="secondary" 
                  size="lg" 
                  onClick={scrollToDownload}
                  icon={<ArrowRight size={20} />}
                  iconPosition="right"
                >
                  Hemen Başla
                </Button>
                <div className="relative inline-block p-1 rounded-full bg-gradient-to-r from-primary-500/20 via-primary-400/20 to-primary-500/20 border border-primary-400/40 shadow-lg shadow-primary-500/30">
                  <div className="absolute inset-0 bg-primary-500/40 rounded-full blur-xl opacity-60"></div>
                  <Button 
                    variant="primary" 
                    size="lg" 
                    className="relative shadow-lg shadow-primary-500/50 hover:shadow-primary-500/70"
                    icon={<PlayCircle size={20} />}
                    onClick={() => window.open('https://www.youtube.com/watch?v=9DCg2pHC0Ww', '_blank')}
                  >
                    Tanıtım Filmini İzle
                  </Button>
                </div>
              </motion.div>
            </div>

            {/* Sağ Taraf: Görsel / Telefon Mockup */}
            <div className="lg:w-1/2 relative overflow-visible">
              <motion.div 
                className="relative z-10"
                initial={{ opacity: 0, scale: 0.8, rotate: -5 }}
                animate={{ opacity: 1, scale: 1, rotate: 0 }}
                transition={{ duration: 0.8, delay: 0.4, type: "spring" }}
              >
                {/* Logo Badge (Görselin üzerine eklenen küçük detay) */}
                <div className="absolute -top-6 -left-6 z-20 bg-white/10 backdrop-blur-md border border-white/20 p-4 rounded-2xl shadow-2xl animate-float">
                   <img src="/exereyes_logo_whitebr.png" alt="Logo" className="w-12 h-12 object-contain" />
                </div>

                <img 
                  src={images.appInterface1}
                  alt="Exereyes Mobil Uygulama Arayüzü" 
                  className="w-full max-w-3xl mx-auto rounded-[2.5rem] shadow-2xl shadow-secondary-500/20 border-8 border-primary-800/50"
                />
              </motion.div>
              
              {/* Görsel Arkası Glow */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-gradient-to-tr from-secondary-500/30 to-primary-500/30 rounded-full blur-3xl -z-10"></div>
            </div>
          </div>
        </div>
      </section>
      
      {/* FEATURES SECTION: Light Background for Contrast */}
      <Section
        variant="light"
        withPattern
        title="Yapay Zeka Destekli Kişisel Antrenörünüz"
        subtitle="Exereyes, görüntü işleme teknolojisi ile evdeki sıradan eşyaları egzersiz ekipmanına dönüştüren, maliyetsiz, mekandan bağımsız ve yapay zeka destekli kişisel antrenörlük hizmeti sunan mobil uygulamadır."
        centered
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          <FeatureCard
            title="Sıfır Ekipman Politikası"
            description="Pahalı spor aletlerine ihtiyaç duymadan, evdeki atıl duran nesneleri (su şişesi, sandalye, kitap) analiz ederek bunları birer spor ekipmanına dönüştürür."
            icon={<Package size={24} />}
            delay={0.1}
          />
          <FeatureCard
            title="Ortam Farkındalıklı Nesne Dönüştürme"
            description="Telefon kamerasıyla ortamı tarayarak evdeki sıradan nesneleri birer egzersiz ekipmanına dönüştürebilme özelliği."
            icon={<Filter size={24} />}
            delay={0.2}
          />
          <FeatureCard
            title="3 Boyutlu Robot Eğitmen"
            description="Kullanıcının kişisel verilerine göre özelleştirilen antrenman programları, üç boyutlu bir robot eğitmen tarafından görsel olarak sunulmaktadır."
            icon={<Bot size={24} />}
            delay={0.3}
          />
          <FeatureCard
            title="Çift Yönlü Etkileşim"
            description="Pasif video içerikleri yerine etkileşimli yapay zeka deneyimi. Sistem kullanıcının formunu takip eder ve performansına göre süreci optimize eder."
            icon={<MessageCircle size={24} />}
            delay={0.4}
          />
          <FeatureCard
            title="Güvenlik ve Kişiselleştirilmiş Sağlık Filtresi"
            description="Kullanıcının sakatlık geçmişini ve fiziksel kısıtlarını analiz ederek 'önce zarar verme' ilkesiyle çalışır. Risk oluşturabilecek hareketleri filtreler."
            icon={<Activity size={24} />}
            delay={0.5}
          />
          <FeatureCard
            title="Donanım Bağımsızlığı"
            description="Sadece standart bir akıllı telefon kamerası kullanarak, görüntü işleme teknolojisiyle kullanıcının materyal yapısını modeller."
            icon={<Globe size={24} />}
            delay={0.6}
          />
          <FeatureCard
            title="Yapay Zeka Destekli Analiz"
            description="Kullanıcının egzersiz süreçlerini ve gelişimini yapay zeka destekli analizlerle takip ederek, kişiye özel bir süreç değerlendirmesi sunar."
            icon={<Layers size={24} />}
            delay={0.7}
          />
          <FeatureCard
            title="Mekandan Bağımsız"
            description="Kamerası olan her cihaz ile her yerde spor salonunuz yanınızda. Evde, ofiste veya parkta sınırları kaldırın."
            icon={<Clock size={24} />}
            delay={0.8}
          />
          <FeatureCard
            title="Herkes İçin Spor"
            description="Gençlik ve Spor Bakanlığı'nın 'Herkes İçin Spor' hedeflerini destekleyerek kadınlar, yaşlılar, gençler ve kırsal bölgelerde yaşayan bireyler için spora erişim engelini ortadan kaldırır."
            icon={<Award size={24} />}
            delay={0.9}
          />
        </div>
      </Section>

      {/* EXERCISE EXAMPLES: Clean White Section */}
      <Section 
        variant="default"
        title="Sıfır Ekipman ile Her Yerde Egzersiz"
        subtitle="Evinizdeki sıradan nesneleri (sandalye, şişe, çanta) kullanarak evde, ofiste veya parkta sınırları kaldırın. Ekipman maliyeti ve mekan engelini ortadan kaldırarak sporu herkes için erişilebilir kılıyoruz."
        centered
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            { img: images.exerciseWaterBottle, title: "Su Şişesi ile Biceps", desc: "Ofiste kısa bir mola verin." },
            { img: images.exerciseBackpack, title: "Çanta ile Squat", desc: "Ağırlık setine ihtiyacınız yok." },
            { img: images.exerciseChair, title: "Sandalye ile Triceps", desc: "Ev eşyalarını verimli kullanın." }
          ].map((item, index) => (
            <motion.div
              key={index}
              className="group relative rounded-2xl overflow-hidden shadow-lg h-80 cursor-pointer"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <img 
                src={item.img} 
                alt={item.title} 
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-primary-900/90 via-primary-900/20 to-transparent opacity-80 group-hover:opacity-90 transition-opacity duration-300"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6 translate-y-2 group-hover:translate-y-0 transition-transform duration-300">
                <h3 className="text-xl font-bold text-white mb-1">{item.title}</h3>
                <p className="text-primary-200 text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300 delay-100">{item.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* APP INTERFACE: Gradient Background */}
      <Section variant="gradient">
        <div className="flex flex-col lg:flex-row items-center gap-16">
          <div className="lg:w-1/2 order-2 lg:order-1">
            <div className="relative">
              {/* Dekoratif Arka Plan */}
              <div className="absolute inset-0 bg-white/10 rounded-full blur-3xl transform scale-90"></div>
              
              <div className="grid grid-cols-2 gap-6 relative z-10">
                <motion.img 
                  src={images.appInterface3} 
                  alt="Exereyes Antrenman Ekranı" 
                  className="rounded-2xl shadow-2xl border-4 border-white/10 hover:-translate-y-2 transition-transform duration-300 mt-12"
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                />
                <motion.img 
                  src={images.appInterface4} 
                  alt="Exereyes Detay Ekranı" 
                  className="rounded-2xl shadow-2xl border-4 border-white/10 hover:-translate-y-2 transition-transform duration-300 mb-12"
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                />
              </div>
            </div>
          </div>
          
          <div className="lg:w-1/2 order-1 lg:order-2 text-white">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold mb-6">
                Kullanıcı Dostu <br />
                <span className="text-secondary-400">Modern Arayüz</span>
              </h2>
              <p className="text-lg text-primary-100 mb-8 leading-relaxed">
                %100 yerli mühendislik ile geliştirilen Exereyes, Google Play Store'da yayınlanmış ve kullanıcıyla buluşmuş çalışan bir teknolojidir. Sade ve anlaşılır arayüzü ile:
              </p>
              
              <ul className="space-y-4 mb-8">
                {[
                  "Tek dokunuşla egzersize başlama",
                  "Gerçek zamanlı hareket takibi ve analizi",
                  "Kişiselleştirilmiş antrenman programları",
                  "Gelişim raporları ve performans grafikleri"
                ].map((item, i) => (
                  <li key={i} className="flex items-center text-primary-50">
                    <div className="w-6 h-6 rounded-full bg-secondary-500/20 flex items-center justify-center mr-3 border border-secondary-500/50">
                      <svg className="w-3 h-3 text-secondary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path></svg>
                    </div>
                    {item}
                  </li>
                ))}
              </ul>

              <Button variant="white" onClick={scrollToDownload}>
                Uygulamayı İncele
              </Button>
            </motion.div>
          </div>
        </div>
      </Section>

      {/* DOWNLOAD SECTION: Final CTA */}
      <AppDownload />
    </>
  );
};

export default ExereyesHome;